import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { gsap } from 'gsap';

const Page = styled.div`
  max-width: 1000px;
  margin: 40px auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ImgBox = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Img = styled.img`
  width: 100%;
  height: 360px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: var(--text);
  margin-bottom: 8px;
`;

const Price = styled.div`
  color: var(--accent);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 8px 0 16px;
`;

const Para = styled.p`
  color: var(--text);
  line-height: 1.6;
`;

const SpecList = styled.ul`
  margin-top: 10px;
  color: var(--muted);
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Qty = styled.input`
  width: 64px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
`;

const Btn = styled.button`
  padding: 12px 18px;
  border-radius: 24px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 700;
  cursor: pointer;
`;

const ProductDetail = () => {
  const { slug } = useParams();
  const imgRef = useRef(null);
  const bodyRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const hasPrice = typeof product?.price === 'number';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/products/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load product');
        if (!cancelled) setProduct(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (imgRef.current) gsap.from(imgRef.current, { autoAlpha: 0, y: 20, duration: 1, ease: 'power2.out' });
      if (bodyRef.current) gsap.from(bodyRef.current.children, { autoAlpha: 0, y: 14, duration: 1, ease: 'power2.out', stagger: 0.06 });
    });
    return () => ctx.revert();
  }, [product?.id]);

  if (loading) {
    return (
      <Page>
        <div style={{ gridColumn: '1 / -1', color: 'var(--muted)' }}>Loading…</div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div style={{ gridColumn: '1 / -1', color: '#b91c1c' }}>{error}</div>
      </Page>
    );
  }

  if (!product) {
    return (
      <Page>
        <div style={{ gridColumn: '1 / -1', color: '#fff' }}>
          Product not found. <Link to="/products">Back to Products</Link>
        </div>
      </Page>
    );
  }

  const onAdd = () => {
    const count = Math.max(1, Number(qty) || 1);
    for (let i = 0; i < count; i++) {
      addItem(product.name, product.price);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 900);
  };

  return (
    <Page>
      <ImgBox ref={imgRef}>
        <Img src={product.image} alt={product.name} />
      </ImgBox>
      <div ref={bodyRef}>
        <Title>{product.name}</Title>
        {hasPrice ? <Price>${product.price.toFixed(2)}</Price> : null}
        <Para>{product.longDescription}</Para>
        {product.specs ? (
          <SpecList>
            {Object.entries(product.specs).map(([k, v]) => (
              <li key={k}>
                <strong style={{ color: '#e1d6c0' }}>{k}:</strong> {String(v)}
              </li>
            ))}
          </SpecList>
        ) : null}
        <Actions>
          {hasPrice ? (
            <>
              <Qty type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
              <Btn onClick={onAdd} disabled={added}>{added ? 'Added!' : 'Add to Cart'}</Btn>
              <Link style={{ color: 'var(--accent)' }} to="/cart">Go to Cart</Link>
            </>
          ) : (
            <Link style={{ color: 'var(--accent)', fontWeight: 700 }} to="/contact">Contact for pricing</Link>
          )}
        </Actions>
      </div>
    </Page>
  );
};

export default ProductDetail;
